import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Orcamento } from '../../core/models/workflow.models';

export interface LeadContatoPayload {
  leadId: number | string;
  tipo: 'TELEFONE' | 'WHATSAPP' | 'EMAIL';
  obs: string;
}

@Component({
  selector: 'app-lead-contato-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './lead-contato-modal.component.html',
  styleUrl: './lead-contato-modal.component.scss',
})
export class LeadContatoModalComponent implements OnChanges {
  @Input({ required: true }) aberto = false;
  @Input() lead: Orcamento | null = null;

  @Output() fechar = new EventEmitter<void>();
  @Output() salvar = new EventEmitter<LeadContatoPayload>();
  @Output() encerrar = new EventEmitter<void>();

  tipoContato: LeadContatoPayload['tipo'] = 'WHATSAPP';
  observacao = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['aberto'] || changes['lead']) {
      if (this.aberto) {
        this.tipoContato = 'WHATSAPP';
        this.observacao = '';
      }
    }
  }

  onClose(): void {
    this.fechar.emit();
  }

  onEncerrar(): void {
    this.encerrar.emit();
  }

  onSalvar(): void {
    if (!this.lead) {
      return;
    }
    const payload: LeadContatoPayload = {
      leadId: this.lead.id ?? this.lead.placa,
      tipo: this.tipoContato,
      obs: this.observacao,
    };
    this.salvar.emit(payload);
  }

  getNumeroOrcamento(): string {
    if (!this.lead) {
      return 'ORC-000000';
    }
    const candidate =
      (this.lead as Orcamento & { numeroOrcamento?: string | number }).numeroOrcamento ??
      (this.lead as Orcamento & { numero?: string | number }).numero ??
      (this.lead as Orcamento & { codigo?: string | number }).codigo;
    if (candidate !== undefined && candidate !== null && String(candidate).trim()) {
      return String(candidate);
    }
    return `ORC-${String(this.lead.id).padStart(6, '0')}`;
  }

  getSubtituloVeiculo(): string {
    if (!this.lead) {
      return '—';
    }
    const placa = this.lead.placa ?? '';
    const marca = this.lead.marca ?? '';
    const modelo = this.lead.modelo ?? '';
    const detalhes = [marca, modelo].filter(Boolean).join(' — ');
    return detalhes ? `${placa} — ${detalhes}` : placa || '—';
  }

  getConsultorNome(): string {
    if (!this.lead) {
      return '—';
    }
    const direct =
      (this.lead as Orcamento & { consultorResponsavel?: string }).consultorResponsavel ??
      (this.lead as Orcamento & { consultor?: string }).consultor ??
      (this.lead as Orcamento & { responsavelAtual?: string }).responsavelAtual ??
      (this.lead as Orcamento & { consultorNome?: string }).consultorNome;
    if (direct && String(direct).trim()) {
      return String(direct).trim();
    }
    const nested = (this.lead as Orcamento & { consultor?: { nome?: string } }).consultor?.nome;
    return nested && nested.trim() ? nested.trim() : '—';
  }

  getStatusLabel(): string {
    if (!this.lead) {
      return '—';
    }
    const status =
      (this.lead as Orcamento & { statusAtendimento?: string }).statusAtendimento ??
      this.lead.status ??
      '—';
    return status || '—';
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'Aberto':
        return '#2563eb';
      case 'Aguardando retorno':
        return '#7c3aed';
      case 'Efetuar Contato':
        return '#f59e0b';
      case 'Perdido':
        return '#ef4444';
      case 'Ganho':
        return '#22c55e';
      default:
        return '#64748b';
    }
  }

  getValor(): number | null {
    if (!this.lead) {
      return null;
    }
    const valor =
      (this.lead as Orcamento & { valorOrcamento?: number }).valorOrcamento ??
      (this.lead as Orcamento & { valor?: number }).valor ??
      (this.lead as Orcamento & { total?: number }).total;
    if (valor === undefined || valor === null) {
      return null;
    }
    return Number(valor);
  }

  getDataAbertura(): Date | null {
    if (!this.lead) {
      return null;
    }
    const raw =
      (this.lead as Orcamento & { dataCriacao?: Date | string }).dataCriacao ?? this.lead.criadoEm;
    return raw ? (raw instanceof Date ? raw : new Date(raw)) : null;
  }
}
